import React from 'react';

const Button = ({ 
  className = '', 
  variant = 'default', 
  size = 'md',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  fullWidth = false,
  children, 
  onClick,
  type = 'button',
  ...props 
}) => {
  // Base button classes using design tokens
  const baseClasses = `
    inline-flex items-center justify-center
    font-semibold border rounded-md
    transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    ${fullWidth ? 'w-full' : ''}
  `;

  // Variant classes using Laerdal design tokens
  const getVariantClasses = (variant) => {
    switch (variant) {
      case 'ghost':
        return 'bg-transparent hover:bg-primary-50 text-brand-blue border-transparent focus:ring-primary-400';
      case 'outline':
        return 'bg-transparent hover:bg-primary-50 text-brand-blue border-brand-blue focus:ring-primary-400';
      case 'secondary':
        return 'bg-white hover:bg-primary-50 text-brand-blue border-brand-blue focus:ring-primary-400';
      case 'danger':
        return 'bg-error-500 hover:bg-error-600 text-white border-error-500 focus:ring-error-400';
      default: // primary
        return 'bg-brand-blue hover:bg-brand-darkBlue text-white border-brand-blue focus:ring-primary-400';
    }
  };

  // Size classes using design tokens
  const getSizeClasses = (size) => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm';
      case 'lg':
        return 'px-6 py-3 text-lg';
      default: // md
        return 'px-4 py-2 text-base';
    }
  };

  const buttonClasses = `
    ${baseClasses}
    ${getVariantClasses(variant)}
    ${getSizeClasses(size)}
    ${className}
  `.replace(/\s+/g, ' ').trim();

  // Icon element
  const iconElement = icon && (
    <span className={`${iconPosition === 'right' ? 'ml-2' : 'mr-2'} flex-shrink-0`}>
      {icon}
    </span>
  );

  // Loading spinner
  const loadingSpinner = (
    <svg 
      className="animate-spin -ml-1 mr-3 h-4 w-4" 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading && loadingSpinner}
      {!loading && icon && iconPosition === 'left' && iconElement}
      <span className={loading ? 'opacity-0' : ''}>{children}</span>
      {!loading && icon && iconPosition === 'right' && iconElement}
    </button>
  );
};

export { Button };
